import React, { useEffect } from 'react';
import { Card, Collapse, Divider, Empty, Spin, Tag } from 'antd';
import { useDispatch, useSelector } from "react-redux";
import { listJobsThunk } from "../Feature/JobSchedulingSlice";
import JobConfigurationForm from "./JobConfigurationForm";
import ExecutionHistoryModal from "./ExecutionHistoryModal";

const JobSchedulingSection = ({ spaceId }) => {
    const dispatch = useDispatch();
    const { jobs, loading, error } = useSelector((state) => state.jobScheduling);

    useEffect(() => {
        if (spaceId) {
            dispatch(listJobsThunk(spaceId))
        }
    }, [dispatch, spaceId]);

    if (loading === 'loading') {
        return <Spin />
    }

    if (error) {
        return <div style={{ color: 'red' }}>Error loading job scheduling: {error}</div>
    }

    if (!jobs || jobs.length === 0) {
        return (
            <div>
                <h4>Job Scheduling</h4>
                <Empty
                    description="No scheduled jobs available. The job scheduling system may not be initialized yet."
                    style={{ marginTop: '20px' }}
                />
                <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#fff7e6', borderRadius: '4px' }}>
                    <p><strong>Note:</strong> Job definitions should be automatically created when the application starts. If they're not showing up:</p>
                    <ul>
                        <li>Check the backend logs for any errors during initialization</li>
                        <li>Try refreshing the page</li>
                        <li>Restart the backend server</li>
                    </ul>
                </div>
            </div>
        );
    }

    // Filter and validate jobs - only show configurable jobs
    // Handle both camelCase (jobType) and snake_case (job_type) property names
    const validJobs = Array.isArray(jobs) ? jobs.filter(job => {
        if (!job) return false;
        // Support both camelCase and snake_case property names
        const jobType = job.jobType || job.job_type;
        console.log('Job:', job, 'jobType:', jobType);
        return jobType === 'NOTIFICATION_DIGEST' || jobType === 'WEEKLY_AVAILABILITY';
    }) : [];

    const getJobDescription = (jobType) => {
        switch (jobType) {
            case 'NOTIFICATION_DIGEST':
                return 'Configure how often you receive notification digest emails';
            case 'WEEKLY_AVAILABILITY':
                return 'Configure when weekly availability reminders are sent';
            default:
                return '';
        }
    };

    const getJobTitle = (jobType) => {
        switch (jobType) {
            case 'NOTIFICATION_DIGEST':
                return '📧 Notification Digest';
            case 'WEEKLY_AVAILABILITY':
                return '📅 Weekly Availability';
            default:
                return jobType;
        }
    };

    const items = validJobs.map((job) => {
        // Support both camelCase and snake_case property names
        const jobType = job.jobType || job.job_type;
        const jobName = job.jobName || job.job_name;
        const currentSchedule = job.currentSchedule || job.current_schedule;
        const notificationFrequency = currentSchedule?.notificationFrequency || currentSchedule?.notification_frequency;
        const weeklyAvailabilityDay = currentSchedule?.weeklyAvailabilityDay || currentSchedule?.weekly_availability_day;
        const lastExecutionTime = currentSchedule?.lastExecutionTime || currentSchedule?.last_execution_time;
        const enabled = currentSchedule?.enabled;

        return {
            key: jobType,
            label: getJobTitle(jobType),
            children: (
                <div>
                    <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                        <div><strong>Description:</strong> {getJobDescription(jobType)}</div>
                        {currentSchedule ? (
                            <>
                                <div style={{ marginTop: '8px' }}>
                                    <strong>Status:</strong> {' '}
                                    <Tag color={enabled ? 'green' : 'red'}>
                                        {enabled ? '✓ Enabled' : '✗ Disabled'}
                                    </Tag>
                                </div>
                                {jobType === 'NOTIFICATION_DIGEST' && notificationFrequency && (
                                    <div style={{ marginTop: '8px' }}>
                                        <strong>Frequency:</strong> {notificationFrequency}
                                    </div>
                                )}
                                {jobType === 'WEEKLY_AVAILABILITY' && weeklyAvailabilityDay && (
                                    <div style={{ marginTop: '8px' }}>
                                        <strong>Day:</strong> {weeklyAvailabilityDay}
                                    </div>
                                )}
                                <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#f0f0f0', borderRadius: '3px' }}>
                                    <strong>📋 Last Execution:</strong> {lastExecutionTime ? new Date(lastExecutionTime).toLocaleString() : 'Never executed'}
                                </div>
                            </>
                        ) : (
                            <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff7e6', borderRadius: '3px', color: '#d46b08' }}>
                                <strong>No configuration set yet</strong>
                            </div>
                        )}
                    </div>
                    <Divider />
                    <JobConfigurationForm spaceId={spaceId} jobType={jobType} />
                    <Divider />
                    <ExecutionHistoryModal spaceId={spaceId} jobType={jobType} jobName={jobName} />
                </div>
            )
        };
    });

    if (validJobs.length === 0) {
        return <Empty description="No configurable jobs available" />
    }

    return (
        <>
            <h4>Job Scheduling</h4>
            <p style={{ color: '#666', marginBottom: '16px' }}>
                Configure how often notification emails are sent and when weekly availability reminders should be delivered.
            </p>
            <Collapse items={items} />
        </>
    );
};

export default JobSchedulingSection;
